"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../../supabaseClient";
import Header from "../../../components/IntranetHeader";
import Footer from "../../../components/Footer";
import { formatProfileName } from "../../../utils/profileName";

function normaliseRoleplayNhsNumber(value) {
  return String(value || "")
    .replace(/\D/g, "")
    .slice(0, 10);
}

function formatRoleplayNhsNumber(value) {
  const digits = normaliseRoleplayNhsNumber(value);
  if (digits.length !== 10) return digits || "Not assigned";
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}

function calculateAgeFromDob(value, onDate = new Date()) {
  if (!value) return "";

  const dob = new Date(`${value}T00:00:00`);
  if (Number.isNaN(dob.getTime())) return "";

  let age = onDate.getFullYear() - dob.getFullYear();
  const monthDifference = onDate.getMonth() - dob.getMonth();

  if (
    monthDifference < 0 ||
    (monthDifference === 0 && onDate.getDate() < dob.getDate())
  ) {
    age -= 1;
  }

  return age >= 0 ? String(age) : "";
}

function todayIsoDate() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function normaliseText(value) {
  return String(value || "").trim().toLowerCase();
}

function candidateKey(candidate) {
  const nhs = normaliseRoleplayNhsNumber(candidate.roleplayNhsNumber);
  if (candidate.portalAccountId) return `portal:${candidate.portalAccountId}`;
  if (nhs) return `nhs:${nhs}`;
  if (candidate.robloxUsername) {
    return `roblox:${normaliseText(candidate.robloxUsername)}`;
  }
  if (candidate.patientId) return `patient:${candidate.patientId}`;
  return `${candidate.roleplayName}:${candidate.fullName}`;
}

export default function CreateAppointment({ session, profile }) {
  const [patients, setPatients] = useState({
    fullName: "",
    dateOfBirth: "",
    age: "",
    robloxUsername: "",
    roleplayName: "",
    discordName: "",
    discordId: "",
  });

  const [appointment, setAppointment] = useState({
    department: "Outpatients",
    clinician: "",
    datetime: "",
    duration: 30,
    location: "Prince George University Hospital",
    address: "",
    preparation: "",
    notes: "",
  });

  const [clinicians, setClinicians] = useState([]);
  const [loading, setLoading] = useState(false);

  const [patientSearch, setPatientSearch] = useState("");
  const [patientSearchLoading, setPatientSearchLoading] = useState(false);
  const [patientSearchError, setPatientSearchError] = useState("");
  const [patientSearchMessage, setPatientSearchMessage] = useState("");
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [selectedPortalAccountId, setSelectedPortalAccountId] = useState(null);
  const [selectedRoleplayNhsNumber, setSelectedRoleplayNhsNumber] = useState("");

  const submittingRef = useRef(false);

  useEffect(() => {
    const fetchClinicians = async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, name_prefix, position, role, suspended")
        .order("full_name", { ascending: true });

      if (error) {
        console.error("Unable to load clinicians:", error);
        setClinicians([]);
        return;
      }

      const staffOnly = (data || []).filter((person) => {
        const position = normaliseText(person.position);
        const role = normaliseText(person.role);

        return (
          position !== "patient" &&
          role !== "patient" &&
          person.suspended !== true
        );
      });

      setClinicians(staffOnly);
    };

    fetchClinicians();
  }, []);

  const selectedAge = useMemo(() => {
    const ageDate = appointment.datetime
      ? new Date(appointment.datetime)
      : new Date();

    return calculateAgeFromDob(patients.dateOfBirth, ageDate) || patients.age;
  }, [appointment.datetime, patients.dateOfBirth, patients.age]);

  const clearSelectedPatientLink = () => {
    setSelectedPatientId(null);
    setSelectedPortalAccountId(null);
    setSelectedRoleplayNhsNumber("");
    setPatientSearchMessage("");
  };

  const buildCandidates = (patientRows, portalRows) => {
    const portals = portalRows || [];
    const patientsFound = patientRows || [];
    const candidates = new Map();
    const usedPortalIds = new Set();

    const findPortalForPatient = (patient) => {
      const patientNhs = normaliseRoleplayNhsNumber(patient.roleplay_nhs_number);
      const patientRoblox = normaliseText(patient.roblox_username);

      return portals.find((portal) => {
        if (patient.portal_account_id && portal.id === patient.portal_account_id) {
          return true;
        }

        const portalNhs = normaliseRoleplayNhsNumber(portal.roleplay_nhs_number);
        const portalRoblox = normaliseText(portal.roblox_username);

        return (
          (patientNhs && portalNhs === patientNhs) ||
          (patientRoblox && portalRoblox === patientRoblox)
        );
      });
    };

    patientsFound.forEach((patient) => {
      const portal = findPortalForPatient(patient);
      if (portal?.id) usedPortalIds.add(portal.id);

      const dateOfBirth = portal?.date_of_birth || "";
      const ageFromDob = calculateAgeFromDob(dateOfBirth);

      const candidate = {
        patientId: patient.id || null,
        portalAccountId: portal?.id || patient.portal_account_id || null,
        roleplayNhsNumber:
          portal?.roleplay_nhs_number || patient.roleplay_nhs_number || "",
        fullName:
          patient.full_name ||
          portal?.roleplay_name ||
          portal?.roblox_display_name ||
          "",
        roleplayName:
          portal?.roleplay_name ||
          patient.roleplay_name ||
          patient.full_name ||
          "",
        robloxUsername:
          portal?.roblox_username || patient.roblox_username || "",
        dateOfBirth,
        age:
          ageFromDob ||
          (patient.age === null || patient.age === undefined
            ? ""
            : String(patient.age)),
        discordId: portal?.discord_id || "",
        discordUsername: portal?.discord_username || "",
        hasPatientRecord: true,
      };

      candidates.set(candidateKey(candidate), candidate);
    });

    portals.forEach((portal) => {
      if (usedPortalIds.has(portal.id)) return;

      const candidate = {
        patientId: null,
        portalAccountId: portal.id,
        roleplayNhsNumber: portal.roleplay_nhs_number || "",
        fullName:
          portal.roleplay_name || portal.roblox_display_name || portal.roblox_username || "",
        roleplayName: portal.roleplay_name || "",
        robloxUsername: portal.roblox_username || "",
        dateOfBirth: portal.date_of_birth || "",
        age: calculateAgeFromDob(portal.date_of_birth),
        discordId: portal.discord_id || "",
        discordUsername: portal.discord_username || "",
        hasPatientRecord: false,
      };

      candidates.set(candidateKey(candidate), candidate);
    });

    return Array.from(candidates.values()).sort((a, b) =>
      String(a.roleplayName || a.fullName).localeCompare(
        String(b.roleplayName || b.fullName),
      ),
    );
  };

  const searchForPatient = async () => {
    const searchValue = patientSearch.trim();

    setPatientSearchError("");
    setPatientSearchMessage("");
    setPatientSearchResults([]);

    if (!searchValue) {
      setPatientSearchError(
        "Enter a roleplay name, roleplay NHS number or Roblox username.",
      );
      return;
    }

    setPatientSearchLoading(true);

    try {
      const digits = normaliseRoleplayNhsNumber(searchValue);
      const isRoleplayNhsNumber = digits.length === 10;

      let patientRows = [];
      let portalRows = [];

      if (isRoleplayNhsNumber) {
        const [patientResult, portalResult] = await Promise.all([
          supabase
            .from("patients")
            .select(
              "id, full_name, age, roblox_username, roleplay_name, roleplay_nhs_number, portal_account_id, arrival_time",
            )
            .eq("roleplay_nhs_number", digits)
            .order("arrival_time", { ascending: false })
            .limit(10),
          supabase
            .from("patient_portal_accounts")
            .select(
              "id, roleplay_nhs_number, roleplay_name, roblox_username, roblox_display_name, date_of_birth, discord_id, discord_username, created_at",
            )
            .eq("roleplay_nhs_number", digits)
            .order("created_at", { ascending: false })
            .limit(10),
        ]);

        if (patientResult.error) throw patientResult.error;
        if (portalResult.error) throw portalResult.error;

        patientRows = patientResult.data || [];
        portalRows = portalResult.data || [];
      } else {
        const pattern = `%${searchValue}%`;

        const [
          patientRoleplayResult,
          patientRobloxResult,
          patientFullNameResult,
          portalRoleplayResult,
          portalRobloxResult,
        ] = await Promise.all([
          supabase
            .from("patients")
            .select(
              "id, full_name, age, roblox_username, roleplay_name, roleplay_nhs_number, portal_account_id, arrival_time",
            )
            .ilike("roleplay_name", pattern)
            .order("arrival_time", { ascending: false })
            .limit(20),
          supabase
            .from("patients")
            .select(
              "id, full_name, age, roblox_username, roleplay_name, roleplay_nhs_number, portal_account_id, arrival_time",
            )
            .ilike("roblox_username", pattern)
            .order("arrival_time", { ascending: false })
            .limit(20),
          supabase
            .from("patients")
            .select(
              "id, full_name, age, roblox_username, roleplay_name, roleplay_nhs_number, portal_account_id, arrival_time",
            )
            .ilike("full_name", pattern)
            .order("arrival_time", { ascending: false })
            .limit(20),
          supabase
            .from("patient_portal_accounts")
            .select(
              "id, roleplay_nhs_number, roleplay_name, roblox_username, roblox_display_name, date_of_birth, discord_id, discord_username, created_at",
            )
            .ilike("roleplay_name", pattern)
            .order("created_at", { ascending: false })
            .limit(20),
          supabase
            .from("patient_portal_accounts")
            .select(
              "id, roleplay_nhs_number, roleplay_name, roblox_username, roblox_display_name, date_of_birth, discord_id, discord_username, created_at",
            )
            .ilike("roblox_username", pattern)
            .order("created_at", { ascending: false })
            .limit(20),
        ]);

        const results = [
          patientRoleplayResult,
          patientRobloxResult,
          patientFullNameResult,
          portalRoleplayResult,
          portalRobloxResult,
        ];
        const firstError = results.find((result) => result.error)?.error;
        if (firstError) throw firstError;

        const patientMap = new Map();
        [
          ...(patientRoleplayResult.data || []),
          ...(patientRobloxResult.data || []),
          ...(patientFullNameResult.data || []),
        ].forEach((row) => {
          if (row?.id) patientMap.set(row.id, row);
        });

        const portalMap = new Map();
        [
          ...(portalRoleplayResult.data || []),
          ...(portalRobloxResult.data || []),
        ].forEach((row) => {
          if (row?.id) portalMap.set(row.id, row);
        });

        patientRows = Array.from(patientMap.values());
        portalRows = Array.from(portalMap.values());
      }

      const candidates = buildCandidates(patientRows, portalRows);
      setPatientSearchResults(candidates);

      if (!candidates.length) {
        setPatientSearchMessage("No matching patient was found.");
      } else if (candidates.length === 1) {
        setPatientSearchMessage("1 matching patient found. Select the patient below.");
      } else {
        setPatientSearchMessage(
          `${candidates.length} matching patients found. Select the correct patient below.`,
        );
      }
    } catch (error) {
      console.error("Unable to search for patient:", error);
      setPatientSearchError("The patient search could not be completed.");
    } finally {
      setPatientSearchLoading(false);
    }
  };

  const selectPatient = async (candidate) => {
    let dateOfBirth = candidate.dateOfBirth || "";
    let discordId = candidate.discordId || "";
    let discordUsername = candidate.discordUsername || "";

    // Always resolve the linked portal account when one exists. This makes
    // Discord details available even when the patient was matched through an
    // older hospital patient row rather than directly through the portal row.
    if (candidate.portalAccountId) {
      try {
        const { data: portalRows, error: portalError } = await supabase
          .from("patient_portal_accounts")
          .select("date_of_birth, discord_id, discord_username")
          .eq("id", candidate.portalAccountId)
          .limit(1);

        if (portalError) throw portalError;

        const portal = portalRows?.[0] || null;
        if (portal) {
          dateOfBirth = portal.date_of_birth || dateOfBirth;
          discordId = portal.discord_id || discordId;
          discordUsername = portal.discord_username || discordUsername;
        }
      } catch (error) {
        console.error("Unable to load the selected patient's portal details:", error);
      }
    }

    const age = calculateAgeFromDob(dateOfBirth) || candidate.age || "";

    setPatients({
      fullName: candidate.fullName || candidate.roleplayName || "",
      dateOfBirth,
      age,
      robloxUsername: candidate.robloxUsername || "",
      roleplayName: candidate.roleplayName || candidate.fullName || "",
      discordName: discordUsername,
      discordId,
    });

    setSelectedPatientId(candidate.patientId || null);
    setSelectedPortalAccountId(candidate.portalAccountId || null);
    setSelectedRoleplayNhsNumber(
      normaliseRoleplayNhsNumber(candidate.roleplayNhsNumber),
    );
    setPatientSearchResults([]);
    setPatientSearchError("");

    const discordMessage = discordId
      ? ` Discord details were also filled from the patient's portal profile${
          discordUsername ? ` (@${discordUsername}).` : "."
        }`
      : " No Discord ID is stored on the patient's portal profile.";

    setPatientSearchMessage(
      `${candidate.roleplayName || candidate.fullName || "Patient"} selected. Stored details have been filled in.${discordMessage}`,
    );
  };

  const handleDobChange = (value) => {
    setPatients((current) => ({
      ...current,
      dateOfBirth: value,
      age: calculateAgeFromDob(value),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);

    try {
      let portalAccountId = selectedPortalAccountId;
      let patientId = selectedPatientId;

      if (!portalAccountId && patients.robloxUsername.trim()) {
        const { data: portalAccounts, error: portalLookupError } = await supabase
          .from("patient_portal_accounts")
          .select("id, roleplay_nhs_number, date_of_birth, discord_id, discord_username")
          .ilike("roblox_username", patients.robloxUsername.trim())
          .limit(1);

        if (portalLookupError) throw portalLookupError;

        const matchedPortal = portalAccounts?.[0] || null;
        portalAccountId = matchedPortal?.id || null;

        if (!selectedRoleplayNhsNumber && matchedPortal?.roleplay_nhs_number) {
          setSelectedRoleplayNhsNumber(
            normaliseRoleplayNhsNumber(matchedPortal.roleplay_nhs_number),
          );
        }
      }

      if (!patientId && selectedRoleplayNhsNumber) {
        const { data: patientRows, error: patientLookupError } = await supabase
          .from("patients")
          .select("id, portal_account_id")
          .eq("roleplay_nhs_number", selectedRoleplayNhsNumber)
          .order("arrival_time", { ascending: false })
          .limit(1);

        if (patientLookupError) throw patientLookupError;

        patientId = patientRows?.[0]?.id || null;
        portalAccountId =
          portalAccountId || patientRows?.[0]?.portal_account_id || null;
      }

      const appointmentDate = appointment.datetime
        ? new Date(appointment.datetime)
        : new Date();
      const numericAge = Number(
        calculateAgeFromDob(patients.dateOfBirth, appointmentDate),
      );

      if (!patients.dateOfBirth || !Number.isFinite(numericAge)) {
        throw new Error("A valid patient date of birth is required.");
      }

      const { data, error } = await supabase
        .from("appointments")
        .insert([
          {
            patient_id: patientId,
            portal_account_id: portalAccountId,
            patient_full_name: patients.fullName,
            patient_age: numericAge,
            patient_dob: patients.dateOfBirth,
            roblox_username: patients.robloxUsername || null,
            roleplay_name: patients.roleplayName || null,
            discord_name: patients.discordName || null,
            discord_id: patients.discordId || null,
            department: appointment.department,
            clinician: appointment.clinician || null,
            appointment_datetime: appointment.datetime,
            duration_minutes: appointment.duration,
            location: appointment.location || "Prince George University Hospital",
            address: appointment.address,
            preparation: appointment.preparation,
            notes: appointment.notes,
            appointment_status: "Scheduled",
            status: true,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      if (patients.discordId && appointment.clinician) {
        const dmResponse = await fetch("/api/send-appointment-dm", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            appointmentId: data.id,
            patientDiscordId: patients.discordId,
            clinicianProfileId: appointment.clinician,
          }),
        });

        if (!dmResponse.ok) {
          const dmError = await dmResponse.json().catch(() => null);
          console.error("Appointment DM failed:", dmError);
        }
      }

      alert("Appointment created successfully!");

      setPatients({
        fullName: "",
        dateOfBirth: "",
        age: "",
        robloxUsername: "",
        roleplayName: "",
        discordName: "",
        discordId: "",
      });

      setAppointment({
        department: "Outpatients",
        clinician: "",
        datetime: "",
        duration: 30,
        location: "Prince George University Hospital",
        address: "",
        preparation: "",
        notes: "",
      });

      setPatientSearch("");
      setPatientSearchResults([]);
      setSelectedPatientId(null);
      setSelectedPortalAccountId(null);
      setSelectedRoleplayNhsNumber("");
      setPatientSearchMessage("");
      setPatientSearchError("");
    } catch (error) {
      console.error(error);
      alert(error?.message || "Error creating appointment.");
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header session={session} profile={profile} />

      <div className="w-full max-w-4xl mx-auto p-6">
        <h1 className="nhsuk-heading-l mb-6">Create Appointment</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <fieldset className="border border-gray-300 p-4 rounded space-y-4">
            <legend className="font-bold text-lg">Patient</legend>

            <div className="nhsuk-form-group">
              <label className="nhsuk-label" htmlFor="appointment-patient-search">
                Find patient
              </label>
              <div className="nhsuk-hint" id="appointment-patient-search-hint">
                Search by roleplay name, 10-digit RUHT roleplay NHS number, or
                Roblox username. Selecting a patient fills their stored details
                and date of birth. Discord username and ID remain blank.
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) auto",
                  alignItems: "stretch",
                  gap: "12px",
                  width: "100%",
                  maxWidth: "720px",
                }}
              >
                <input
                  id="appointment-patient-search"
                  type="text"
                  className="nhsuk-input"
                  value={patientSearch}
                  onChange={(event) => {
                    setPatientSearch(event.target.value);
                    clearSelectedPatientLink();
                    setPatientSearchError("");
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      searchForPatient();
                    }
                  }}
                  placeholder="Roleplay name, NHS number or Roblox username"
                  aria-describedby="appointment-patient-search-hint"
                  style={{
                    width: "100%",
                    minWidth: 0,
                    height: "48px",
                    minHeight: "48px",
                    margin: 0,
                  }}
                />

                <button
                  type="button"
                  className="nhsuk-button nhsuk-button--secondary"
                  onClick={searchForPatient}
                  disabled={patientSearchLoading}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "48px",
                    minHeight: "48px",
                    margin: 0,
                    padding: "8px 20px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {patientSearchLoading ? "Searching…" : "Find patient"}
                </button>
              </div>

              {patientSearchError && (
                <div className="nhsuk-error-message mt-3" role="alert">
                  {patientSearchError}
                </div>
              )}

              {patientSearchMessage && (
                <div className="nhsuk-inset-text mt-3">{patientSearchMessage}</div>
              )}

              {patientSearchResults.length > 0 && (
                <div className="overflow-x-auto mt-4">
                  <table className="nhsuk-table">
                    <thead>
                      <tr>
                        <th>Roleplay name</th>
                        <th>Roleplay NHS number</th>
                        <th>Roblox</th>
                        <th>Date of birth</th>
                        <th>Age</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {patientSearchResults.map((candidate) => (
                        <tr key={candidateKey(candidate)}>
                          <td>
                            <strong>
                              {candidate.roleplayName || candidate.fullName || "Patient"}
                            </strong>
                          </td>
                          <td>
                            {formatRoleplayNhsNumber(candidate.roleplayNhsNumber)}
                          </td>
                          <td>
                            {candidate.robloxUsername
                              ? `@${candidate.robloxUsername}`
                              : "Not recorded"}
                          </td>
                          <td>{candidate.dateOfBirth || "Not provided"}</td>
                          <td>{candidate.age || "Not available"}</td>
                          <td>
                            <button
                              type="button"
                              className="nhsuk-button nhsuk-button--secondary"
                              onClick={() => selectPatient(candidate)}
                              style={{ margin: 0 }}
                            >
                              Select
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {selectedRoleplayNhsNumber && (
              <div className="nhsuk-inset-text">
                <strong>Selected roleplay NHS number:</strong>{" "}
                {formatRoleplayNhsNumber(selectedRoleplayNhsNumber)}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="nhsuk-label" htmlFor="appointment-patient-full-name">
                  Full Name
                </label>
                <input
                  id="appointment-patient-full-name"
                  type="text"
                  className="nhsuk-input w-full"
                  value={patients.fullName}
                  onChange={(event) =>
                    setPatients((current) => ({
                      ...current,
                      fullName: event.target.value,
                    }))
                  }
                  required
                />
              </div>

              <div>
                <label className="nhsuk-label" htmlFor="appointment-patient-dob">
                  Date of Birth
                </label>
                <input
                  id="appointment-patient-dob"
                  type="date"
                  className="nhsuk-input w-full"
                  value={patients.dateOfBirth}
                  max={todayIsoDate()}
                  onChange={(event) => handleDobChange(event.target.value)}
                  required
                />
                <div className="nhsuk-hint">
                  The patient's age is calculated automatically from this date and the appointment date.
                </div>
              </div>

              <div>
                <label className="nhsuk-label" htmlFor="appointment-patient-age">
                  Age
                </label>
                <input
                  id="appointment-patient-age"
                  type="number"
                  className="nhsuk-input w-full"
                  value={selectedAge}
                  readOnly
                  aria-readonly="true"
                />
              </div>

              <div>
                <label className="nhsuk-label" htmlFor="appointment-patient-roblox">
                  Roblox Username
                </label>
                <input
                  id="appointment-patient-roblox"
                  type="text"
                  className="nhsuk-input w-full"
                  value={patients.robloxUsername}
                  onChange={(event) =>
                    setPatients((current) => ({
                      ...current,
                      robloxUsername: event.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label className="nhsuk-label" htmlFor="appointment-patient-roleplay-name">
                  Roleplay Name
                </label>
                <input
                  id="appointment-patient-roleplay-name"
                  type="text"
                  className="nhsuk-input w-full"
                  value={patients.roleplayName}
                  onChange={(event) =>
                    setPatients((current) => ({
                      ...current,
                      roleplayName: event.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label className="nhsuk-label" htmlFor="appointment-patient-discord-name">
                  Discord Username
                </label>
                <input
                  id="appointment-patient-discord-name"
                  type="text"
                  className="nhsuk-input w-full"
                  value={patients.discordName}
                  onChange={(event) =>
                    setPatients((current) => ({
                      ...current,
                      discordName: event.target.value,
                    }))
                  }
                  placeholder="Enter manually if required"
                />
              </div>

              <div>
                <label className="nhsuk-label" htmlFor="appointment-patient-discord-id">
                  Discord ID
                </label>
                <input
                  id="appointment-patient-discord-id"
                  type="text"
                  className="nhsuk-input w-full"
                  value={patients.discordId}
                  onChange={(event) =>
                    setPatients((current) => ({
                      ...current,
                      discordId: event.target.value,
                    }))
                  }
                  placeholder="Enter manually if a DM should be sent"
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="border border-gray-300 p-4 rounded space-y-4">
            <legend className="font-bold text-lg">Appointment</legend>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="nhsuk-label" htmlFor="appointment-department">
                  Department
                </label>
                <input
                  id="appointment-department"
                  type="text"
                  className="nhsuk-input w-full"
                  value={appointment.department}
                  onChange={(event) =>
                    setAppointment((current) => ({
                      ...current,
                      department: event.target.value,
                    }))
                  }
                  required
                />
              </div>

              <div>
                <label className="nhsuk-label" htmlFor="appointment-clinician">
                  Clinician
                </label>
                <select
                  id="appointment-clinician"
                  className="nhsuk-select w-full"
                  value={appointment.clinician}
                  onChange={(event) =>
                    setAppointment((current) => ({
                      ...current,
                      clinician: event.target.value,
                    }))
                  }
                  required
                >
                  <option value="">-- Select Clinician --</option>
                  {clinicians.map((clinician) => (
                    <option key={clinician.id} value={clinician.id}>
                      {formatProfileName(clinician)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="nhsuk-label" htmlFor="appointment-datetime">
                  Date & Time
                </label>
                <input
                  id="appointment-datetime"
                  type="datetime-local"
                  className="nhsuk-input w-full"
                  value={appointment.datetime}
                  onChange={(event) =>
                    setAppointment((current) => ({
                      ...current,
                      datetime: event.target.value,
                    }))
                  }
                  required
                />
              </div>

              <div>
                <label className="nhsuk-label" htmlFor="appointment-duration">
                  Duration (minutes)
                </label>
                <input
                  id="appointment-duration"
                  type="number"
                  className="nhsuk-input w-full"
                  value={appointment.duration}
                  onChange={(event) =>
                    setAppointment((current) => ({
                      ...current,
                      duration: Number.parseInt(event.target.value, 10) || 1,
                    }))
                  }
                  min={1}
                />
              </div>

              <div>
                <label className="nhsuk-label" htmlFor="appointment-location">
                  Location
                </label>
                <select
                  id="appointment-location"
                  className="nhsuk-select w-full"
                  value={appointment.location}
                  onChange={(event) =>
                    setAppointment((current) => ({
                      ...current,
                      location: event.target.value,
                    }))
                  }
                  required
                >
                  <option value="Prince George University Hospital">
                    Prince George University Hospital
                  </option>
                  <option value="Duke of York Hospital">
                    Duke of York Hospital
                  </option>
                </select>
              </div>

              <div>
                <label className="nhsuk-label" htmlFor="appointment-address">
                  Address
                </label>
                <input
                  id="appointment-address"
                  type="text"
                  className="nhsuk-input w-full"
                  value={appointment.address}
                  onChange={(event) =>
                    setAppointment((current) => ({
                      ...current,
                      address: event.target.value,
                    }))
                  }
                />
              </div>

              <div className="md:col-span-2">
                <label className="nhsuk-label" htmlFor="appointment-preparation">
                  Preparation (optional)
                </label>
                <textarea
                  id="appointment-preparation"
                  className="nhsuk-textarea w-full"
                  value={appointment.preparation}
                  onChange={(event) =>
                    setAppointment((current) => ({
                      ...current,
                      preparation: event.target.value,
                    }))
                  }
                />
              </div>

              <div className="md:col-span-2">
                <label className="nhsuk-label" htmlFor="appointment-notes">
                  Notes (optional)
                </label>
                <textarea
                  id="appointment-notes"
                  className="nhsuk-textarea w-full"
                  value={appointment.notes}
                  onChange={(event) =>
                    setAppointment((current) => ({
                      ...current,
                      notes: event.target.value,
                    }))
                  }
                />
              </div>
            </div>
          </fieldset>

          <button type="submit" className="nhsuk-button w-full" disabled={loading}>
            {loading ? "Creating..." : "Create Appointment"}
          </button>
        </form>
      </div>

      <Footer />
    </div>
  );
}
